/**
 * プレイスラベル（地図の吹き出し）の2段目を出すための計算。
 *
 * 仕様: 吹き出しは2行。1行目はプレイス名、2行目は管理画面で設定した表示項目の1番目の値。
 * 「1番目」はシステム設定 `place_disp_item` の並びの先頭で、利用者側に並べ替えのUIは無い
 * （ユーザー側の `user_disp_item` と同じ扱い。2026-08-20 の打ち合わせの決定4）。
 *
 * 現行のプレイスラベルはサーバー生成のMVTタイル（`properties.label` = プレイス名）を
 * 共通ライブラリが描いている。値を含む2行にするには画面側で描き直す必要があるため、
 * タイル側のラベル表示・ズーム範囲・地物の絞り込みをここで再現する。
 *
 * 値の文字列化とアイコンは `PlaceLabelItemText.js` が持つ（i18n を使うため）。
 * ここは他のモジュールに依存しない計算だけを持つ（単体テストのため）。
 */

import {
  DEFAULT_AUTO_CHECKIN_RANGE_INDOOR,
  DEFAULT_AUTO_CHECKIN_RANGE_OUTDOOR,
  MAP_LAYER_REFERENCE_TABLE_TYPE,
} from '@/constants.js'

/**
 * プレイスリスト表示項目のシステム設定キー
 * 管理画面（IFIELD_V4-7149）が `place_code|tel|fax` のようなパイプ区切りで保存する
 * @type {String}
 */
export const PLACE_DISP_ITEM_KEY = 'place_disp_item'

/**
 * プレイスの表示項目の種別
 *
 * 管理画面側の設計（IFIELD_V4-7149 基本設計V2）に合わせている。
 * 屋内・プレイス属性・センサー値・紐付けセンサー名はユーザーの任意項目と同じスキーマで返る。
 * グループ・担当者・住所は選択式の項目ではなく、カードの固定ヘッダー側に出るため含まない
 * @type {Object.<String, String>}
 */
export const PLACE_ITEM_TYPE = {
  PLACE_CODE: 'place_code',
  CHECKIN_RADIUS: 'checkin_radius',
  TEL: 'tel',
  FAX: 'fax',
  INDOOR: 'indoor',
  ATTRIBUTE: 'attribute',
  SENSOR_DATA: 'sensor_data',
  // ユーザー側に遅れて足された項目（IFIELD_V4-7247）。管理画面はプレイスでも選べる
  SENSOR_NAMES: 'sensor_names',
}

/**
 * 値の後ろに付ける単位
 *
 * センサー値がレスポンスの `unit` を値の後ろに出すのと同じ形にする。
 * リストのカードと地図のラベルで同じものを使う（IFIELD_V4-7276）
 * @type {Object.<String, String>}
 */
export const PLACE_ITEM_UNIT = {
  [PLACE_ITEM_TYPE.CHECKIN_RADIUS]: 'm',
}

/**
 * 値に単位を付けた文字列を返す
 * @param {String} type 表示項目の種別（`PLACE_ITEM_TYPE`）
 * @param {String|Number|null} value 値
 * @returns {String} 単位を付けた文字列。値が無いときは空文字
 */
export const withPlaceItemUnit = (type, value) => {
  if (value == null || value === '') return ''
  const unit = PLACE_ITEM_UNIT[type]
  return unit ? `${value} ${unit}` : `${value}`
}

/**
 * プレイスに値が無いときに使われるシステムの既定値を返す
 *
 * 自動チェックイン半径は、プレイスに値を持たせなければシステムの既定値で動く。
 * 値が無いからと「-」を出すと実際の動きと食い違うので、既定値を出す。
 * 既定値は屋内と屋外で違う。プレイス詳細（`AppPanelPlaceDetailInfo.vue`）と同じ求め方
 * @param {String} type 表示項目の種別（`PLACE_ITEM_TYPE`）
 * @param {Object} place プレイス
 * @returns {Number|null} 既定値。既定値を持たない項目は null
 */
export const getPlaceItemDefaultValue = (type, place) => {
  if (type !== PLACE_ITEM_TYPE.CHECKIN_RADIUS) return null
  return place?.indoor_level_id
    ? DEFAULT_AUTO_CHECKIN_RANGE_INDOOR
    : DEFAULT_AUTO_CHECKIN_RANGE_OUTDOOR
}

/**
 * マップレイヤーの参照先がプレイスグループの場合、参照先テーブルIDはプレイスグループID。
 * プレイス一覧APIの `place_groups` と突き合わせると、地物がどのレイヤーのものか分かる
 * @type {Number}
 */
const PLACE_REFERENCE_TABLE_TYPE = MAP_LAYER_REFERENCE_TABLE_TYPE.PLACE

/**
 * ラベルの描画データに持たせるレイヤーのキー
 *
 * 表示ズーム範囲もレイヤーメニューの表示切替もマップレイヤー単位に効くが、
 * マップレイヤーごとに描画レイヤーを分けると、レイヤーの数だけ描画物が増える
 * （実データでは百数十枚あり、IFIELD_V4-7146 で潰したのと同じ状態になる）。
 * そのため地物ごとに表示可否を判定して、描くものだけを1組のレイヤーで描く
 * @param {Number|String} mapLayerId マップレイヤーID
 * @returns {String} レイヤーのキー
 */
export const toLabelLayerKey = (mapLayerId) => `place-${mapLayerId}`

/**
 * プレイスのマップレイヤーだけを取り出す
 * @param {Array.<Object>} mapLayers マップレイヤーのマスタ
 * @returns {Array.<Object>} プレイスのマップレイヤー
 */
export const findPlaceMapLayers = (mapLayers) =>
  (mapLayers || []).filter(
    (layer) => layer?.reference_table_type == PLACE_REFERENCE_TABLE_TYPE
  )

/**
 * プレイスグループIDからマップレイヤーを引く表を作る
 * @param {Array.<Object>} placeMapLayers プレイスのマップレイヤー
 * @returns {Map.<Number, Object>} プレイスグループID => マップレイヤー
 */
export const buildMapLayerByPlaceGroupId = (placeMapLayers) =>
  new Map(
    (placeMapLayers || []).map((layer) => [layer.reference_table_id, layer])
  )

/**
 * プレイスが属するマップレイヤーを返す
 *
 * 1つのプレイスが複数のグループに属することがあるため、
 * 先に見つかったレイヤーを採用する
 * @param {Object} place プレイス
 * @param {Map.<Number, Object>} mapLayerByPlaceGroupId プレイスグループIDからの対応表
 * @returns {Object|null} マップレイヤー
 */
export const findPlaceMapLayer = (place, mapLayerByPlaceGroupId) => {
  const groups = place?.place_groups
  if (!Array.isArray(groups) || !mapLayerByPlaceGroupId) return null
  for (const group of groups) {
    const layer = mapLayerByPlaceGroupId.get(group?.place_group_id)
    if (layer) return layer
  }
  return null
}

/**
 * そのズームレベルでラベルを出すか
 *
 * 判定は共通ライブラリのマップレイヤー（`MVTSubLayerRenderer` の `_getLabelVisible`）に
 * 揃えてある。範囲の端は含み、`null` は「制限なし」として扱う
 * @param {Number} zoom ズームレベル
 * @param {Number|null} minZoom 最小ズームレベル
 * @param {Number|null} maxZoom 最大ズームレベル
 * @returns {Boolean} 出す場合はtrue
 */
export const isLabelVisibleAtZoom = (zoom, minZoom, maxZoom) => {
  if (zoom == null) return true
  if (minZoom != null && zoom < minZoom) return false
  if (maxZoom != null && zoom > maxZoom) return false
  return true
}

/**
 * そのマップレイヤーのラベルを描くか
 *
 * レイヤーメニューの表示切替と、マップレイヤーのラベル表示ズーム範囲で決まる。
 * 設定が引けないレイヤー（地図に並んでいないレイヤー）のラベルは描かない
 * @param {Object|undefined} setting レイヤーの表示設定（`buildLabelLayerSettings` の値）
 * @param {Number} zoom ズームレベル
 * @returns {Boolean} 描く場合はtrue
 */
export const isLabelLayerVisible = (setting, zoom) =>
  !!setting &&
  setting.visible !== false &&
  isLabelVisibleAtZoom(zoom, setting.labelMinZoom, setting.labelMaxZoom)

/**
 * ラベルの表示可否をレイヤー単位で取り出す
 *
 * 地図画面はマップレイヤーの描画パラメータを自前で持っている（`UseMapLayerFilter`）。
 * タイル側のラベルと同じ見え方にするため、**そのパラメータをそのまま判定に使う**。
 * 判定材料を別に組み立てると、レイヤーメニューの切替や屋内フロアの絞り込みとずれる
 * @param {Array.<Object>} renderParameters レイヤー描画パラメータ
 * @param {Array.<Object>} mapLayers マップレイヤーのマスタ
 * @param {String} mvtLayerId MVTレイヤーのID（共通ライブラリの `MapLayerMVTRenderer.layerId`）
 * @returns {Object.<String, Object>} レイヤーのキー => { visible, filter, labelMinZoom, labelMaxZoom }
 */
export const buildLabelLayerSettings = ({
  renderParameters,
  mapLayers,
  mvtLayerId,
}) => {
  const settings = {}
  for (const param of renderParameters || []) {
    if (param?.id !== mvtLayerId) continue
    // MVTレイヤー自体が非表示なら、その下のプレイスは1件も出さない
    const parentVisible = param.visible !== false
    for (const subParam of param.subLayers || []) {
      const layer = (mapLayers || []).find((l) => l.id === +subParam?.id)
      if (!layer || layer.reference_table_type != PLACE_REFERENCE_TABLE_TYPE) {
        continue
      }
      settings[toLabelLayerKey(layer.id)] = {
        visible: parentVisible && isLabelVisible(subParam.visible),
        filter: subParam.filter || null,
        labelMinZoom: layer.label_min_zoom,
        labelMaxZoom: layer.label_max_zoom,
      }
    }
  }
  return settings
}

/**
 * レイヤーの表示状態からラベルの表示可否を取り出す
 *
 * 表示状態は `{ geometry, label }` のオブジェクトだが、
 * 真偽値だけが入っていることもある（共通ライブラリの既定値）
 * @param {Object|Boolean} visible レイヤーの表示状態
 * @returns {Boolean} ラベルを出す場合はtrue
 */
const isLabelVisible = (visible) => {
  if (visible == null) return true
  if (typeof visible === 'object') return !!visible.label
  return !!visible
}

/**
 * マーカーの上に持ち上げない場合のずらし量
 * @type {Array.<Number>}
 */
const NO_PIXEL_OFFSET = [0, 0]

/**
 * マーカーの上にラベルを持ち上げるジオメトリ種別
 *
 * タイル側は MVT のジオメトリ種別で判定していて、MVT には点・線・面の3種しか無い。
 * `MultiPoint` はタイルでは点の地物として届き、共通ライブラリからは `Point` に見える
 * （ローカルの実画面で確認済み。2点の `MULTIPOINT` でもタイル側は `Point` を返す）。
 * 画面側は プレイス一覧API の GeoJSON をそのまま見るため `MultiPoint` のまま届くので、
 * ここで同じ扱いに寄せる。線・面は `MultiLineString` / `MultiPolygon` でも
 * タイル側が持ち上げないため、区別する必要がない
 * @type {Array.<String>}
 */
const MARKER_GEOMETRY_TYPES = ['Point', 'MultiPoint']

/**
 * ラベル全体のずらし量を返す
 *
 * タイル側のラベル（共通ライブラリの `MVTSubLayerRenderer._getLabelOffset`）は、
 * **点のときだけ**マーカーの高さぶんラベルを持ち上げる。
 * 面・線にはマーカーが無く、ラベルは図形の最北点にそのまま置かれる。
 * 画面側で描き直すラベルも同じ規則に揃える（IFIELD_V4-7275）
 * @param {String} geometryType ジオメトリ種別（GeoJSON の `geometry.type`）
 * @param {Array.<Number>} pointOffset 点のときに使うずらし量（マップレイヤーのスタイル）
 * @returns {Array.<Number>} ずらし量
 */
export const getLabelPixelOffset = (geometryType, pointOffset) =>
  MARKER_GEOMETRY_TYPES.includes(geometryType) && Array.isArray(pointOffset)
    ? pointOffset
    : NO_PIXEL_OFFSET
